/**
 * The letter tab: the legal verdict, the draft, and the way into the composer.
 *
 * THE VERDICT IS SHOWN BEFORE ANYTHING ELSE AND BEFORE ANY BUTTON IS PRESSED.
 * A tool that only reveals "this company may not be written to" after an
 * operator has asked for a letter has wasted their attention twice, and the
 * second time it also spent tokens. On `blocked` the generate button is not
 * disabled, it is absent: a greyed-out button is an invitation to look for the
 * way around it, and here there is deliberately none.
 *
 * THE RECIPIENT IS NEVER TYPED HERE. `to` arrives from the API, decided by the
 * server's gate, and the hand-over to the composer asks for it again rather
 * than reusing what is on screen. A company that unsubscribed since the draft
 * was written must not have its address carried across by a React state field.
 *
 * The stale-evidence refusal is a first-class state, not an error toast. It is
 * the case where the tool is working correctly and the answer is "not from this
 * run" - the operator can re-run the brain, or confirm and send an older letter
 * whose disproven claims have already been dropped from it.
 */
import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Ban, Check, Copy, Loader2, Mail, PenLine, Send } from "lucide-react";
import { Button } from "@wa/ui";
import { ApiError } from "../../../../lib/api";
import {
  buildBrainReport,
  generateBrainEmail,
  getBrainLegal,
  suppressBrainCompany,
  type BrainCtaVariant,
  type BrainEmailDraft,
  type BrainEmailRefusal,
  type BrainFreshness,
  type BrainLegalView,
} from "../../../../lib/siteBrainEmail";
import type { BrainReportInfo } from "../../../../lib/siteBrain";
import { StatusPill, useToast } from "../../../ui";
import type { T } from "../../site-scanner/types";

const VARIANTS: BrainCtaVariant[] = ["call", "demo", "report"];

/** The states that carry no draft. Everything else may be written. */
const isBlocked = (legal: BrainLegalView | null): boolean => legal?.state === "blocked";

export function BrainEmail({
  domain,
  canManage,
  draft: stored,
  report,
  hasSynthesis,
  onChanged,
  t,
}: {
  domain: string;
  canManage: boolean;
  draft: BrainEmailDraft | null;
  report: BrainReportInfo | null;
  hasSynthesis: boolean;
  /** Ask the card to re-read the row, so the stored draft and report refresh. */
  onChanged: () => void;
  t: T;
}) {
  const toast = useToast();
  const [legal, setLegal] = useState<BrainLegalView | null>(null);
  const [variant, setVariant] = useState<BrainCtaVariant>("call");
  const [busy, setBusy] = useState(false);
  const [stale, setStale] = useState<BrainFreshness | null>(null);
  const [copied, setCopied] = useState(false);

  // Asked on mount rather than on the first click. It is one cheap query, and
  // knowing early is the entire value: the answer changes whether the operator
  // spends the next ten minutes on this company at all.
  useEffect(() => {
    let alive = true;
    void getBrainLegal(domain)
      .then((next) => alive && setLegal(next))
      .catch(() => alive && setLegal(null));
    return () => {
      alive = false;
    };
  }, [domain]);

  const write = useCallback(
    async (force: boolean) => {
      setBusy(true);
      setStale(null);
      try {
        await generateBrainEmail(domain, variant, force);
        onChanged();
        toast(t("brain.email.written"));
      } catch (err) {
        const body = err instanceof ApiError ? (err.body as BrainEmailRefusal | undefined) : undefined;
        if (body?.error === "stale_evidence" && body.freshness) {
          // Not a toast. This is a decision the operator has to make with the
          // detail in front of them, so it stays on screen until they act.
          setStale(body.freshness);
        } else {
          toast(t(`brain.email.error.${body?.error ?? "pass_failed"}`));
        }
      } finally {
        setBusy(false);
      }
    },
    [domain, variant, onChanged, toast, t],
  );

  const refuse = useCallback(async () => {
    setBusy(true);
    try {
      await suppressBrainCompany(domain);
      const next = await getBrainLegal(domain);
      setLegal(next);
      toast(t("brain.email.suppressed"));
    } catch {
      toast(t("brain.email.error.pass_failed"));
    } finally {
      setBusy(false);
    }
  }, [domain, toast, t]);

  const makeReport = useCallback(async () => {
    setBusy(true);
    try {
      await buildBrainReport(domain);
      onChanged();
      toast(t("brain.report.built"));
    } catch (err) {
      const code = err instanceof ApiError ? String(err.message) : "failed";
      toast(t(code === "nothing_to_show" ? "brain.report.nothingToShow" : "brain.email.error.pass_failed"));
    } finally {
      setBusy(false);
    }
  }, [domain, onChanged, toast, t]);

  const copyReport = useCallback(() => {
    if (!report) return;
    // Absolute, because the point of this string is that it is pasted into a
    // letter and opened on somebody else's machine.
    const url = `${window.location.origin}/api/public/brain-report/${report.token}`;
    void navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    });
  }, [report]);

  return (
    <div className="space-y-4">
      <LegalBanner legal={legal} t={t} />

      {!hasSynthesis ? (
        <p className="text-sm text-muted-foreground">{t("brain.email.needsSynthesis")}</p>
      ) : null}

      {canManage && hasSynthesis && !isBlocked(legal) ? (
        <div className="flex flex-wrap items-center gap-2">
          {/* Three asks, not one. A single hard "20 minutes on a call" is the
              request ignored by exactly the companies worth the most. */}
          {VARIANTS.map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setVariant(value)}
              className={
                value === variant
                  ? "rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground"
                  : "rounded-full border border-border px-3 py-1 text-xs text-muted-foreground hover:text-foreground"
              }
            >
              {t(`brain.email.variant.${value}`)}
            </button>
          ))}

          <Button size="sm" variant="outline" onClick={() => void write(false)} disabled={busy}>
            {busy ? <Loader2 className="size-3.5 animate-spin" /> : <PenLine className="size-3.5" />}
            {t(stored ? "brain.email.rewrite" : "brain.email.write")}
          </Button>

          <Button size="sm" variant="ghost" onClick={() => void refuse()} disabled={busy}>
            <Ban className="size-3.5" />
            {t("brain.email.refuse")}
          </Button>
        </div>
      ) : null}

      {stale ? <StaleNotice freshness={stale} busy={busy} onConfirm={() => void write(true)} t={t} /> : null}

      {stored ? <DraftView draft={stored} domain={domain} t={t} /> : null}

      {hasSynthesis ? (
        <ReportRow
          report={report}
          canManage={canManage}
          busy={busy}
          copied={copied}
          onBuild={() => void makeReport()}
          onCopy={copyReport}
          t={t}
        />
      ) : null}
    </div>
  );
}

/** Whether we may write here, and what the letter will therefore carry. */
function LegalBanner({ legal, t }: { legal: BrainLegalView | null; t: T }) {
  if (!legal) return null;
  const blocked = legal.state === "blocked";

  return (
    <section
      className="space-y-1 rounded-2xl bg-[var(--crm-sunk)] px-4 py-3"
      style={{
        boxShadow: `inset 3px 0 0 0 color-mix(in oklab, ${
          blocked ? "var(--color-destructive)" : "var(--color-success)"
        } 70%, transparent)`,
      }}
    >
      <div className="flex flex-wrap items-center gap-2">
        {blocked ? (
          <Ban className="size-4 shrink-0" style={{ color: "var(--color-destructive)" }} />
        ) : (
          <Mail className="size-4 shrink-0" style={{ color: "var(--color-success)" }} />
        )}
        <span className="text-sm font-semibold">{t(`brain.email.legal.${legal.state}`)}</span>
        {legal.address ? <StatusPill tone="mut">{legal.address}</StatusPill> : null}
        {legal.needsPrivacyNotice ? <StatusPill tone="wt">{t("brain.email.privacyNotice")}</StatusPill> : null}
      </div>

      <p className="text-xs text-muted-foreground">{t(`brain.email.reason.${legal.reason}`)}</p>

      {/* "We may not write to them" is not something an operator can act on.
          "They unsubscribed from a campaign in June" is. */}
      {legal.suppression ? (
        <p className="text-xs text-muted-foreground">
          {t("brain.email.suppressedOn", {
            value: legal.suppression.value,
            date: new Date(legal.suppression.at).toLocaleDateString(),
          })}
        </p>
      ) : null}
    </section>
  );
}

/** The proofs that no longer hold, and the one decision they force. */
function StaleNotice({
  freshness,
  busy,
  onConfirm,
  t,
}: {
  freshness: BrainFreshness;
  busy: boolean;
  onConfirm: () => void;
  t: T;
}) {
  const dead = freshness.checks.filter((c) => c.verdict === "stale");
  return (
    <section className="space-y-2 rounded-xl border border-[var(--color-warning)]/40 bg-[var(--crm-sunk)] px-4 py-3">
      <p className="flex items-center gap-2 text-sm font-medium">
        <AlertTriangle className="size-4 shrink-0" style={{ color: "var(--color-warning)" }} />
        {t(dead.length ? "brain.email.staleEvidence" : "brain.email.staleSynthesis")}
      </p>

      {dead.length ? (
        <ul className="space-y-0.5 text-xs text-muted-foreground">
          {dead.map((check) => (
            <li key={check.findingId}>
              {check.url ?? check.findingId}
              {check.detail ? ` (${check.detail})` : ""}
            </li>
          ))}
        </ul>
      ) : null}

      {/* Confirming is permission to send an older letter, never permission to
          keep a disproven claim: the server drops those arguments either way. */}
      <Button size="sm" variant="outline" onClick={onConfirm} disabled={busy}>
        {t("brain.email.writeAnyway")}
      </Button>
    </section>
  );
}

/** The letter itself, and the way into the composer. */
function DraftView({ draft, domain, t }: { draft: BrainEmailDraft; domain: string; t: T }) {
  return (
    <section className="space-y-2 rounded-xl border border-border bg-card/60 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium">{draft.subject}</p>
        <div className="flex items-center gap-2">
          <StatusPill tone="mut">{t(`brain.email.variant.${draft.variant}`)}</StatusPill>
          {draft.sentAt ? (
            <StatusPill tone="ok">
              <Send className="size-3" /> {new Date(draft.sentAt).toLocaleDateString()}
            </StatusPill>
          ) : null}
        </div>
      </div>

      {/* Plain text, in a monospace-free pre so the mandatory blocks are read as
          part of the letter rather than as machinery underneath it. */}
      <pre className="whitespace-pre-wrap break-words text-sm leading-relaxed">{draft.body}</pre>

      <div className="flex flex-wrap items-center gap-2 pt-1">
        {/* A link, not a fetch-and-fill: the composer asks the server for the
            draft itself, so the recipient is decided one more time on the way
            in rather than carried across from this screen. */}
        <a
          className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium hover:bg-accent"
          href={`/email?brain=${encodeURIComponent(domain)}`}
        >
          <Mail className="size-3.5" />
          {t("brain.email.openComposer")}
        </a>
        <span className="text-xs text-muted-foreground">
          {t("brain.email.generatedOn", { date: new Date(draft.generatedAt).toLocaleString() })}
        </span>
      </div>
    </section>
  );
}

/** The sendable page: build it, copy the link, see whether it was opened. */
function ReportRow({
  report,
  canManage,
  busy,
  copied,
  onBuild,
  onCopy,
  t,
}: {
  report: BrainReportInfo | null;
  canManage: boolean;
  busy: boolean;
  copied: boolean;
  onBuild: () => void;
  onCopy: () => void;
  t: T;
}) {
  return (
    <section className="space-y-2 rounded-xl border border-dashed border-border px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium">{t("brain.report.title")}</p>
        {canManage ? (
          <Button size="sm" variant="ghost" onClick={onBuild} disabled={busy}>
            {busy ? <Loader2 className="size-3.5 animate-spin" /> : null}
            {t(report ? "brain.report.rebuild" : "brain.report.build")}
          </Button>
        ) : null}
      </div>

      {report ? (
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <button
            type="button"
            onClick={onCopy}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1 hover:bg-accent"
          >
            {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
            {t(copied ? "brain.report.copied" : "brain.report.copyLink")}
          </button>
          <a
            className="underline-offset-2 hover:text-foreground hover:underline"
            href={`/api/public/brain-report/${report.token}`}
            target="_blank"
            rel="noreferrer"
          >
            {t("brain.report.preview")}
          </a>
          {/* A number that is honestly soft: a prefetching mail client opens the
              page without a human ever looking at it, so it is worded as
              openings rather than as readers. */}
          <span>{t("brain.report.opens", { n: report.opens })}</span>
          {report.lastOpenAt ? <span>{new Date(report.lastOpenAt).toLocaleString()}</span> : null}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">{t("brain.report.hint")}</p>
      )}
    </section>
  );
}
